import { FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Stack } from "@mui/system";

import { Modal } from "@/components";
import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";

import {
	PreviewSeatChangeResponse,
	useChangeLinkedinAmountMutation,
	useGetPricingQuery,
	usePreviewSeatChangeMutation,
} from "@/pages/Private/redux/subscription/subscription.api";

import { InputField } from "@/components/InputField/InputField";

import {
	createSeatTotalSchema,
	parseSeatInput,
	SeatTotalForm,
	SenderSeatModalProps,
	toInitialSeatTotal,
	toPaidAmount,
	toSeatTotal,
} from "./senderSeats.helpers";

export const EditLinkedinModal: FunctionComponent<SenderSeatModalProps> = ({
	isOpen,
	setIsOpen,
	companyId,
	paidAmount,
	baseAmount,
	bonusAmount,
	pendingAmount,
	pendingEffectiveAt,
}) => {
	const { t } = useTranslation();
	const [successMessage, setSuccessMessage] = useState<string | null>(null);
	const [preview, setPreview] = useState<PreviewSeatChangeResponse | null>(null);

	const [editLinkedinAmount, { isLoading, error }] = useChangeLinkedinAmountMutation();
	const { data: pricingData } = useGetPricingQuery();
	const [previewSeatChange, { isLoading: isLoadingPreview }] = usePreviewSeatChangeMutation();

	const schema = useMemo(() => createSeatTotalSchema(baseAmount), [baseAmount]);

	const {
		setValue,
		handleSubmit,
		formState: { errors },
		watch,
	} = useForm<SeatTotalForm>({
		defaultValues: {
			amount: toInitialSeatTotal(paidAmount, baseAmount, pendingAmount),
		},
		resolver: zodResolver(schema),
	});

	const values = watch();

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	const fetchPreview = useCallback(
		async (seatTotal: number) => {
			const newPaidAmount = toPaidAmount(seatTotal, baseAmount);

			if (newPaidAmount < 0) {
				setPreview(null);

				return;
			}

			try {
				const result = await previewSeatChange({
					type: "linkedin",
					amount: newPaidAmount,
				}).unwrap();

				setPreview(result);
			} catch {
				setPreview(null);
			}
		},
		[baseAmount, previewSeatChange]
	);

	useEffect(() => {
		const timeout = setTimeout(() => {
			fetchPreview(values.amount);
		}, 500);

		return () => clearTimeout(timeout);
	}, [values.amount, fetchPreview]);

	const onSubmit = async (values: SeatTotalForm) => {
		try {
			const result = await editLinkedinAmount({
				amount: toPaidAmount(values.amount, baseAmount),
				companyId,
			}).unwrap();

			if (result.immediate) {
				setSuccessMessage("Your subscription has been updated.");
			} else if (result.pendingEffectiveAt) {
				const effectiveDate = new Date(result.pendingEffectiveAt).toLocaleDateString("de-DE", {
					day: "2-digit",
					month: "2-digit",
					year: "numeric",
				});

				setSuccessMessage(
					`Your seat count will be reduced to ${result.pendingAmount} on ${effectiveDate}.`
				);
			}

			setTimeout(() => {
				setSuccessMessage(null);
				setIsOpen(false);
			}, 2000);
		} catch (err) {
			console.error(err);
		}
	};

	// Format price from pricing API
	const getPriceDisplay = () => {
		if (pricingData?.linkedinPrice) {
			const price = (pricingData.linkedinPrice.unitAmount / 100).toFixed(2);
			const interval = pricingData.interval === "year" ? "year" : "month";

			return `€${price}/${interval}`;
		}

		return "€39.00/month"; // Fallback
	};

	// Format pending effective date
	const formatPendingDate = () => {
		if (pendingEffectiveAt) {
			return new Date(pendingEffectiveAt).toLocaleDateString("de-DE", {
				day: "2-digit",
				month: "2-digit",
				year: "numeric",
			});
		}

		return "";
	};

	return (
		<Modal
			handleClose={() => {
				setSuccessMessage(null);
				setPreview(null);
				setIsOpen(false);
			}}
			disableSave={preview?.changeType === "no_change" || isLoadingPreview}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isLoading}
			isOpened={isOpen}
			submitButtonText={t("basics.confirm")}
			title={"Edit number of LinkedIn accounts"}
		>
			<Stack spacing={2}>
				{successMessage && (
					<div className="rounded-md bg-green-50 p-3 text-sm text-green-700">{successMessage}</div>
				)}

				{pendingAmount !== null && pendingAmount !== undefined && !successMessage && (
					<div className="rounded-md bg-yellow-50 p-3 text-sm text-yellow-700">
						Pending change: Seats will decrease to <b>{toSeatTotal(pendingAmount, baseAmount)}</b>{" "}
						on {formatPendingDate()}.
					</div>
				)}

				<InputField
					error={!!errors.amount?.message}
					handleChange={(e) => {
						setValue("amount", parseSeatInput(e.target.value, baseAmount), {
							shouldValidate: true,
						});
					}}
					label={"Your total number of paid LinkedIn Accounts"}
					min={baseAmount}
					name={"amount"}
					placeholder={"Enter the amount of LinkedIn accounts you want to have"}
					type="number"
					value={values.amount}
				/>

				<p className="text-sm text-text-meta">
					{baseAmount} LinkedIn account(s) are included in your subscription.
				</p>

				{bonusAmount > 0 && (
					<p className="text-sm text-text-meta">
						+{bonusAmount} bonus seat(s) granted by your admin (not billed).
					</p>
				)}

				{preview && preview.changeType !== "no_change" && (
					<div className="rounded-md border border-border-light bg-brand-white p-3 text-sm">
						{preview.changeType === "increase" && preview.proratedAmountPreview !== null && (
							<p className="text-text">
								You&apos;ll be charged <b>€{preview.proratedAmountPreview.toFixed(2)}</b> now
								(prorated).
							</p>
						)}
						{preview.changeType === "decrease" && preview.effectiveAt && (
							<p className="text-text">
								Your seat count will decrease on{" "}
								<b>
									{new Date(preview.effectiveAt).toLocaleDateString("de-DE", {
										day: "2-digit",
										month: "2-digit",
										year: "numeric",
									})}
								</b>
								.
							</p>
						)}
						<p className="text-text-meta mt-1">
							{baseAmount} included + {preview.newAmount} paid{" "}
							{preview.bonusSeats ? `+ ${preview.bonusSeats} bonus` : ""} ={" "}
							{preview.newAmount + preview.bonusSeats + baseAmount} total seats
						</p>
					</div>
				)}

				{isLoadingPreview && <p className="text-sm text-text-meta">Calculating preview...</p>}

				<p className="text-sm text-text-meta">
					You can adjust the number of LinkedIn accounts available to all users in your
					organization.
				</p>

				<p className="text-sm text-text-meta">
					Increases take effect immediately and are billed right away. Decreases will take effect at
					the end of your billing cycle.
				</p>

				<p className="text-sm text-text-meta">
					Each LinkedIn account costs <b>{getPriceDisplay()}</b> (excluding VAT).
				</p>

				{pricingData?.isAnnual && (
					<p className="text-sm text-text-meta">Your subscription is billed annually.</p>
				)}

				<p className="text-sm text-text-meta">
					You can reduce the number to <b>{baseAmount}</b> at any time.
				</p>

				{getAllErrors(error, formErrors).length
					? renderErrorMessages(getAllErrors(error, formErrors))
					: null}
			</Stack>
		</Modal>
	);
};
